import { useRef, useState, useCallback } from "react";

/**
 * Shared submit-lock + idempotency-key hook.
 *
 * Guards create flows (Organization, Fund, Role, …) against duplicate
 * submissions from double-click / Enter-mashing:
 *   • `submitting` flips to true on the first call; subsequent calls
 *     (including keyboard-repeat) short-circuit until the handler resolves.
 *   • `run(fn)` invokes the passed async function, passing an
 *     Idempotency-Key that stays stable across retries in the same submit
 *     attempt (a new key is minted only after success or on manual reset).
 *   • On success the key is rotated so the NEXT create gets a fresh key.
 *   • On failure the key is preserved — a retry with the same key lets the
 *     backend deduplicate a partial-success (e.g. record created but
 *     network dropped before response).
 */
export function useIdempotentSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const keyRef = useRef(null);
  const inflightRef = useRef(false);

  const ensureKey = () => {
    if (!keyRef.current) {
      keyRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return keyRef.current;
  };

  const run = useCallback(async (fn) => {
    if (inflightRef.current) return; // hard-block re-entry
    inflightRef.current = true;
    setSubmitting(true);
    const key = ensureKey();
    try {
      const result = await fn(key);
      // rotate on success so the next submit starts fresh
      keyRef.current = null;
      return result;
    } finally {
      inflightRef.current = false;
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    keyRef.current = null;
    inflightRef.current = false;
    setSubmitting(false);
  }, []);

  return { submitting, run, reset };
}
